import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BarChart2, ShieldAlert, Activity, Users, Globe, Crosshair, FileText, Radar, Map, MessageSquareWarning, Fingerprint, Network, Bot, Flag, Briefcase, Gavel, FileSearch, LayoutDashboard, Eye, Megaphone, ExternalLink, Swords, TrendingUp, Settings as SettingsIcon, User, HelpCircle, ChevronDown, Search, Shield, History, CircleDot } from 'lucide-react';
import { Sidebar, SidebarContent, SidebarGroup, SidebarGroupLabel, SidebarGroupContent, SidebarMenu, SidebarMenuItem, SidebarMenuButton, SidebarMenuBadge, SidebarHeader, SidebarFooter, SidebarSeparator } from '../Sidebar';
import { Badge } from '../Badge';
import { Avatar, AvatarFallback, AvatarImage } from '../Avatar';
import { AuditLogSheet } from './AuditLogSheet';
import { CommandSearch } from './CommandSearch';
import { PaspampresLogo } from './PaspampresLogo';
import { getRoleAvatarUrl, getSession, type SessionRole } from '../../lib/auth';
type Item = {
  to: string;
  icon: any;
  label: string;
  badge?: string;
  badgeTone?: 'red' | 'amber' | 'primary' | 'emerald';
};
type Group = {
  id: string;
  label: string;
  items: Item[];
  defaultOpen?: boolean;
};
const groups: Group[] = [{
  id: 'intel',
  label: 'Intelijen Presiden',
  defaultOpen: true,
  items: [{
    to: '/dashboard',
    icon: LayoutDashboard,
    label: 'Executive Dashboard'
  }, {
    to: '/war-room',
    icon: Crosshair,
    label: 'War Room',
    badge: 'LIVE',
    badgeTone: 'red'
  }, {
    to: '/digital-twin',
    icon: Bot,
    label: 'Presidential Digital Twin'
  }, {
    to: '/daily-brief',
    icon: FileText,
    label: 'Daily Brief'
  }, {
    to: '/narrative-calendar',
    icon: LayoutDashboard,
    label: 'Narrative Calendar'
  }, {
    to: '/ai-analyst',
    icon: Bot,
    label: 'AI Presidential Analyst'
  }]
}, {
  id: 'radar',
  label: 'Radar Isu',
  defaultOpen: true,
  items: [{
    to: '/hot-issue',
    icon: Radar,
    label: 'Hot Issue Radar',
    badge: '12',
    badgeTone: 'red'
  }, {
    to: '/sentiment',
    icon: Activity,
    label: 'Sentiment Monitoring'
  }, {
    to: '/shadow-narrative',
    icon: Crosshair,
    label: 'Shadow Narrative'
  }, {
    to: '/public-grievance',
    icon: MessageSquareWarning,
    label: 'Public Grievance'
  }]
}, {
  id: 'threat',
  label: 'Ancaman & Proteksi',
  defaultOpen: true,
  items: [{
    to: '/threat-intel',
    icon: ShieldAlert,
    label: 'Threat Intelligence',
    badge: '5',
    badgeTone: 'amber'
  }, {
    to: '/hybrid-threats',
    icon: Network,
    label: 'Hybrid Threats'
  }, {
    to: '/digital-escort',
    icon: Fingerprint,
    label: 'Digital Escort'
  }, {
    to: '/visit-intel',
    icon: Map,
    label: 'Visit Intelligence'
  }, {
    to: '/deepfake',
    icon: Eye,
    label: 'Deepfake Detection'
  }]
}, {
  id: 'actor',
  label: 'Intelijen Aktor',
  defaultOpen: true,
  items: [{
    to: '/actor-network',
    icon: Network,
    label: 'Actor Network'
  }, {
    to: '/bot-detection',
    icon: Users,
    label: 'Buzzer & Bot'
  }, {
    to: '/foreign-influence',
    icon: Globe,
    label: 'Foreign Influence'
  }]
}, {
  id: 'stability',
  label: 'Stabilitas Nasional',
  defaultOpen: false,
  items: [{
    to: '/ipoleksosbudhankam',
    icon: BarChart2,
    label: 'IPOLEKSOSBUDHANKAM'
  }, {
    to: '/astagatra',
    icon: Flag,
    label: 'Astagatra Digital'
  }, {
    to: '/geospatial',
    icon: Map,
    label: 'Geospatial Intel'
  }, {
    to: '/policy-impact',
    icon: TrendingUp,
    label: 'Policy Impact'
  }, {
    to: '/program-war-room',
    icon: Swords,
    label: 'Program War Room'
  }]
}, {
  id: 'command',
  label: 'Komando & Respons',
  defaultOpen: false,
  items: [{
    to: '/command-tasking',
    icon: Briefcase,
    label: 'Command Tasking'
  }, {
    to: '/crisis-simulation',
    icon: Activity,
    label: 'Crisis Simulation'
  }, {
    to: '/response-recommender',
    icon: Megaphone,
    label: 'Response Recommender'
  }, {
    to: '/ministry-mapping',
    icon: Network,
    label: 'Ministry Mapping'
  }]
}, {
  id: 'legal',
  label: 'Hukum & Evidence',
  defaultOpen: false,
  items: [{
    to: '/legal-evidence',
    icon: Gavel,
    label: 'Legal Evidence'
  }, {
    to: '/platform-reporting',
    icon: ExternalLink,
    label: 'Platform Reporting'
  }, {
    to: '/case-management',
    icon: FileSearch,
    label: 'Case Management'
  }]
}, {
  id: 'workspace',
  label: 'Workspace',
  defaultOpen: false,
  items: [{
    to: '/workbench',
    icon: Briefcase,
    label: 'Analyst Workbench'
  }, {
    to: '/reports',
    icon: FileText,
    label: 'Reports'
  }]
}, {
  id: 'system',
  label: 'Sistem',
  defaultOpen: false,
  items: [{
    to: '/profile',
    icon: User,
    label: 'Profil Pengguna'
  }, {
    to: '/ai-config',
    icon: Bot,
    label: 'AI Configuration'
  }, {
    to: '/settings',
    icon: SettingsIcon,
    label: 'Pengaturan'
  }, {
    to: '/help',
    icon: HelpCircle,
    label: 'Bantuan & FAQ'
  }]
}];
const badgeToneClass: Record<string, string> = {
  red: 'bg-red-100 text-red-700 border border-red-200',
  amber: 'bg-amber-100 text-amber-700 border border-amber-200',
  primary: 'bg-primary/10 text-primary border border-primary/20',
  emerald: 'bg-emerald-100 text-emerald-700 border border-emerald-200'
};
export function AppSidebar() {
  const {
    pathname
  } = useLocation();
  const nav = useNavigate();
  const [auditOpen, setAuditOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [session, setSessionState] = useState<SessionRole>(() => getSession());
  useEffect(() => {
    const onChange = (e: any) => {
      if (e.detail) setSessionState(e.detail as SessionRole);else setSessionState(getSession());
    };
    window.addEventListener('gs_session_change', onChange as EventListener);
    return () => window.removeEventListener('gs_session_change', onChange as EventListener);
  }, []);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    groups.forEach(g => {
      const containsActive = g.items.some(it => it.to === pathname);
      init[g.id] = !(g.defaultOpen || containsActive);
    });
    return init;
  });
  const toggle = (id: string) => setCollapsed(s => ({
    ...s,
    [id]: !s[id]
  }));
  return <>
      <Sidebar variant="inset" collapsible="icon" className="border-r border-border/60 bg-sidebar" data-id="element-245">
        {/* Header — Paspampres seal + brand */}
        <SidebarHeader className="px-3 py-3 border-b border-border/60" data-id="element-246">
          <div className="flex items-center gap-2.5 px-1 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center" data-id="element-247">
            <div className="flex size-10 items-center justify-center rounded-lg bg-white border border-border/60 shrink-0 shadow-sm" data-id="element-248">
              <PaspampresLogo size={36} className="size-9" data-id="element-249" />
            </div>
            <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden" data-id="element-250">
              <p className="font-heading text-[13px] font-bold leading-tight truncate" data-id="element-251">
                Garuda Shield
              </p>
              <p className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground truncate" data-id="element-252">
                Paspampres · C4I
              </p>
            </div>
            <div className="flex items-center gap-1 rounded bg-red-50 border border-red-200 px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-red-700 group-data-[collapsible=icon]:hidden" data-id="element-253">
              <CircleDot className="size-2" data-id="element-254" />
              <span className="hidden xl:inline" data-id="element-255">RHS</span>
            </div>
          </div>

          {/* Quick search trigger */}
          <button onClick={() => setSearchOpen(true)} title="Cari (⌘K)" className="mt-3 w-full group flex items-center gap-2 rounded-md border border-border/60 bg-muted/40 hover:bg-muted/70 hover:border-primary/40 transition px-2.5 py-1.5 text-left group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:mx-auto" data-id="element-256">
            <Search className="size-3.5 text-muted-foreground group-hover:text-foreground transition shrink-0 group-data-[collapsible=icon]:size-4" data-id="element-257" />
            <span className="text-xs text-muted-foreground flex-1 group-data-[collapsible=icon]:hidden" data-id="element-258">
              Cari modul, isu, aktor...
            </span>
            <kbd className="hidden sm:inline-flex font-mono text-[9px] text-muted-foreground bg-background border border-border/60 px-1 py-0.5 rounded group-data-[collapsible=icon]:hidden" data-id="element-259">
              ⌘K
            </kbd>
          </button>
        </SidebarHeader>

        <SidebarContent className="scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent" data-id="element-260">
          {groups.map((g, gi) => {
          const isGroupCollapsed = collapsed[g.id];
          const groupHasActive = g.items.some(it => it.to === pathname);
          return <SidebarGroup key={g.id} className="py-1" data-id="element-261">
                <button type="button" onClick={() => toggle(g.id)} className={`w-full group flex items-center gap-1.5 px-3 py-1.5 rounded-md transition cursor-pointer group-data-[collapsible=icon]:hidden ${groupHasActive ? 'text-foreground hover:bg-muted/60' : 'hover:bg-muted/40'}`} aria-expanded={!isGroupCollapsed} data-id="element-262">
                  <ChevronDown className={`size-3 text-muted-foreground transition-transform duration-200 ${isGroupCollapsed ? '-rotate-90' : ''}`} data-id="element-263" />
                  <span className={`font-mono text-[10px] uppercase tracking-wider transition flex-1 text-left ${groupHasActive ? 'text-foreground font-semibold' : 'text-muted-foreground group-hover:text-foreground'}`} data-id="element-264">
                    {g.label}
                  </span>
                  {groupHasActive && <span className="size-1.5 rounded-full bg-primary shrink-0" data-id="element-265" />}
                  <Badge variant="outline" className="font-mono text-[9px] h-4 px-1.5 border-border/60 bg-transparent text-muted-foreground" data-id="element-266">
                    {g.items.length}
                  </Badge>
                </button>
                {!isGroupCollapsed && <SidebarGroupContent className="pt-0.5" data-id="element-267">
                    <SidebarMenu className="gap-0.5" data-id="element-268">
                      {g.items.map(it => {
                  const active = pathname === it.to;
                  return <SidebarMenuItem key={it.to} data-id="element-269">
                            <SidebarMenuButton asChild isActive={active} tooltip={it.label} className={`group/menu relative h-9 transition-all ${active ? 'bg-primary/10 text-primary font-semibold hover:bg-primary/15 hover:text-primary shadow-sm ring-1 ring-primary/10' : 'text-foreground/80 hover:bg-muted hover:text-foreground'}`} data-id="element-270">
                              <Link to={it.to} className="flex flex-row items-center gap-2.5 px-2 w-full overflow-hidden" data-id="element-271">
                                {active && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-0.5 rounded-r-full bg-primary" data-id="element-272" />}
                                <it.icon className="size-4 shrink-0" data-id="element-273" />
                                <span className="flex-1 min-w-0 truncate text-left text-[13px] group-data-[collapsible=icon]:hidden" data-id="element-274">
                                  {it.label}
                                </span>
                                {it.badge && <SidebarMenuBadge className={`${badgeToneClass[it.badgeTone ?? 'primary']} font-mono text-[9px] h-4 px-1 rounded shrink-0 group-data-[collapsible=icon]:hidden`} data-id="element-275">
                                    {it.badge}
                                  </SidebarMenuBadge>}
                              </Link>
                            </SidebarMenuButton>
                          </SidebarMenuItem>;
                })}
                    </SidebarMenu>
                  </SidebarGroupContent>}
                {gi < groups.length - 1 && <SidebarSeparator className="mt-1 bg-border/40 group-data-[collapsible=icon]:hidden" data-id="element-276" />}
              </SidebarGroup>;
        })}
        </SidebarContent>

        <SidebarFooter className="border-t border-border/60 p-3" data-id="element-277">
          {/* Mini profile */}
          <button onClick={() => nav('/profile')} className="w-full flex items-center gap-2.5 rounded-md p-1.5 hover:bg-muted/50 transition group group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0" title={session.role} data-id="element-278">
            <Avatar className="size-8 border border-border/60 shrink-0" data-id="element-279">
              <AvatarImage src={getRoleAvatarUrl(session.roleId)} data-id="element-280" />
              <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-bold" data-id="element-281">
                {session.initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 text-left group-data-[collapsible=icon]:hidden" data-id="element-282">
              <p className="text-xs font-bold leading-tight truncate" data-id="element-283">
                {session.role}
              </p>
              <p className="font-mono text-[9px] text-muted-foreground truncate" data-id="element-284">
                {session.code} · Aktif
              </p>
            </div>
            <span className="size-1.5 rounded-full bg-emerald-500 shrink-0 group-hover:scale-125 transition group-data-[collapsible=icon]:hidden" data-id="element-285" />
          </button>

          {/* Audit log link */}
          <button onClick={() => setAuditOpen(true)} title="View Audit Log" className="w-full mt-2 flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 hover:bg-muted/50 hover:border-primary/40 px-2.5 py-1.5 transition group group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:h-8 group-data-[collapsible=icon]:p-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:mx-auto" data-id="element-286">
            <History className="size-3.5 text-muted-foreground group-hover:text-foreground transition shrink-0 group-data-[collapsible=icon]:size-4" data-id="element-287" />
            <span className="font-mono text-[10px] text-muted-foreground group-hover:text-foreground transition flex-1 text-left group-data-[collapsible=icon]:hidden" data-id="element-288">
              View Audit Log
            </span>
          </button>
        </SidebarFooter>
      </Sidebar>

      <AuditLogSheet open={auditOpen} onOpenChange={setAuditOpen} data-id="element-289" />
      <CommandSearch open={searchOpen} onOpenChange={setSearchOpen} data-id="element-290" />
    </>;
}